import {
  Activity,
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  BadgePercent,
  Bell,
  Building2,
  ChartLine,
  ClipboardList,
  Coins,
  FileDown,
  Flag,
  Gauge,
  Headset,
  IdCard,
  Landmark,
  Megaphone,
  Network,
  Radar,
  Scale,
  Settings,
  ShieldCheck,
  Smartphone,
  UsersRound,
  Wallet,
  type LucideIcon,
  PiggyBank,
} from 'lucide-react';

export interface AdminNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Phase that delivers this section (EXECUTION_PLAN). */
  phase: number;
}
export interface AdminNavGroup {
  label: string;
  items: AdminNavItem[];
}

/** Daily operations (Phases 3–8). */
export const ADMIN_NAV_LIVE: AdminNavItem[] = [
  { to: '/', label: 'نمای کلی عملیات', icon: Gauge, phase: 3 },
  { to: '/users', label: 'کاربران', icon: UsersRound, phase: 3 },
  { to: '/kyc', label: 'صف احراز هویت', icon: IdCard, phase: 3 },
  { to: '/user-banks', label: 'حساب‌های بانکی کاربران', icon: Landmark, phase: 3 },
];

/**
 * Money operations (ledger, Toman deposit/withdrawal, reconciliation, HopEx accounts).
 * Customer-specific money actions live in Customer 360 («عملیات مالی»), not here.
 */
export const ADMIN_NAV_MONEY: AdminNavItem[] = [
  { to: '/toman-deposits', label: 'واریز تومان', icon: ArrowDownToLine, phase: 4 },
  { to: '/toman-withdrawals', label: 'برداشت تومان', icon: ArrowUpFromLine, phase: 4 },
  { to: '/adjustments', label: 'موجودی و اصلاحیه‌ها', icon: Scale, phase: 4 },
  { to: '/reconciliation', label: 'تطبیق و توقف اضطراری', icon: ShieldCheck, phase: 4 },
  { to: '/hopex-banks', label: 'حساب‌های بانکی هوپکس', icon: Building2, phase: 4 },
];

/** Crypto operations (catalogue, crypto deposit/withdrawal, provenance & risk). */
export const ADMIN_NAV_CRYPTO: AdminNavItem[] = [
  { to: '/crypto-deposits', label: 'واریز رمزارز', icon: Coins, phase: 5 },
  { to: '/crypto-withdrawals', label: 'برداشت رمزارز', icon: Wallet, phase: 5 },
  { to: '/assets', label: 'دارایی‌ها و شبکه‌ها', icon: Network, phase: 5 },
  { to: '/screening', label: 'ریسک و منشأ دارایی', icon: Radar, phase: 5 },
];

/** Market operations (rates, provider health, conversions + treasury notes). */
export const ADMIN_NAV_TRADING: AdminNavItem[] = [
  { to: '/conversions', label: 'خرید / فروش / تبدیل', icon: ArrowLeftRight, phase: 6 },
  { to: '/rates', label: 'نرخ‌ها و سلامت منابع', icon: Activity, phase: 6 },
];

/** Customer communication (Phases 7–8): support console, announcements, notification delivery. */
export const ADMIN_NAV_COMMS: AdminNavItem[] = [
  { to: '/support', label: 'پشتیبانی و گفتگو', icon: Headset, phase: 8 },
  // OD-65: support agents, device pairing codes, paired devices.
  { to: '/desk', label: 'میز پشتیبانی', icon: Smartphone, phase: 9 },
  // OD-69: one wallet address per network (USDT deposit + withdraw; main coins deposit only).
  { to: '/wallets', label: 'کیف پول‌های واریز', icon: Wallet, phase: 9 },
  { to: '/announcements', label: 'اعلان‌ها و بنرها', icon: Megaphone, phase: 8 },
  { to: '/notifications', label: 'عملیات اعلان‌ها', icon: Bell, phase: 8 },
];

/** Reporting and growth (Phase 8): financial report, exports, referral overview (read-only). */
export const ADMIN_NAV_REPORTS: AdminNavItem[] = [
  // Owner 2026-10-17: Σ what all users hold per asset (to source the same amounts behind the scenes).
  { to: '/holdings', label: 'موجودی کل کاربران', icon: PiggyBank, phase: 9 },
  { to: '/reports', label: 'گزارش مالی', icon: ChartLine, phase: 8 },
  { to: '/exports', label: 'خروجی‌ها', icon: FileDown, phase: 8 },
  { to: '/referral', label: 'دعوت از دوستان', icon: BadgePercent, phase: 8 },
];

/** System: integrations board, runtime settings and the audit trail. */
export const ADMIN_NAV_SYSTEM: AdminNavItem[] = [
  { to: '/maintenance', label: 'سلامت سرویس‌ها', icon: Flag, phase: 8 },
  { to: '/settings', label: 'تنظیمات', icon: Settings, phase: 8 },
  { to: '/audit', label: 'گزارش رویدادها', icon: ClipboardList, phase: 3 },
];

/**
 * Sections that are NOT built yet (honestly marked «به‌زودی»; routes render a placeholder that performs
 * no action). Empty since Phase 8 completed referral / reports / exports / settings.
 */
export const ADMIN_NAV_FUTURE: AdminNavGroup[] = [];

/** Every section — routes are generated from this so menu and routes can never drift. */
export const ADMIN_NAV: AdminNavGroup[] = [
  { label: 'عملیات', items: ADMIN_NAV_LIVE },
  { label: 'مالی', items: ADMIN_NAV_MONEY },
  { label: 'رمزارز', items: ADMIN_NAV_CRYPTO },
  { label: 'بازار و تبدیل', items: ADMIN_NAV_TRADING },
  { label: 'ارتباطات و پشتیبانی', items: ADMIN_NAV_COMMS },
  { label: 'گزارش و رشد', items: ADMIN_NAV_REPORTS },
  { label: 'سیستم', items: ADMIN_NAV_SYSTEM },
  ...ADMIN_NAV_FUTURE,
];

export const isFutureSection = (to: string): boolean =>
  ADMIN_NAV_FUTURE.some((g) => g.items.some((i) => i.to === to));
